import { NavLink, Outlet, useNavigate } from 'react-router';
import { useLogout, useMe } from '../api/auth';
import { Logo } from '../components/Logo';
import { Icon, type IconName } from '../components/Icon';
import { TopbarContext, useTopbarState } from './PageTitle';

const NAV: Array<{ to: string; label: string; icon: IconName; count?: string; end?: boolean }> = [
  { to: '/', label: 'Prehľad', icon: 'grid', end: true },
  { to: '/sites', label: 'Stránky', icon: 'server' },
  { to: '/vulnerabilities', label: 'Zraniteľnosti', icon: 'shield' },
  // Dostupnosť je vlastné meranie pripravované vo v2 — miesto v navigácii má už teraz (ticket 12)
  { to: '/uptime', label: 'Dostupnosť', icon: 'pulse', count: 'v2' },
  { to: '/settings', label: 'Nastavenia', icon: 'sliders' },
];

function initials(email: string): string {
  const local = email.split('@')[0] ?? '';
  const parts = local.split(/[._-]+/).filter(Boolean);
  return (parts.length >= 2 ? parts[0][0] + parts[1][0] : local.slice(0, 2)).toUpperCase();
}

/** Shell podľa design/Main.dc.html: sidebar s navigáciou, topbar, scrollovateľný obsah. */
export function AppShell() {
  const me = useMe();
  const logout = useLogout();
  const navigate = useNavigate();
  const topbar = useTopbarState();
  const email = me.data?.email ?? '';

  async function signOut() {
    await logout.mutateAsync();
    navigate('/login', { replace: true });
  }

  return (
    <TopbarContext.Provider value={topbar}>
      <div className="flex h-full">
        <aside className="sidebar">
          <div className="px-2 text-white">
            <Logo />
          </div>
          <nav className="navgroup" aria-label="Hlavná navigácia">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => `nav ${isActive ? 'on' : ''}`}>
                <Icon name={n.icon} />
                <span>{n.label}</span>
                {n.count && <span className="count">{n.count}</span>}
              </NavLink>
            ))}
          </nav>
          <div className="sidefoot">
            <button type="button" className="userchip" onClick={signOut} disabled={logout.isPending}>
              <span className="avatar">{initials(email)}</span>
              <span className="flex min-w-0 flex-col gap-0.5">
                <span className="ue truncate">{email}</span>
                <span className="ul">Odhlásiť sa</span>
              </span>
            </button>
          </div>
        </aside>

        <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
          <header className="topbar">
            <div>
              <div className="tb-title">{topbar.content?.title ?? ''}</div>
              {topbar.content?.sub && <div className="tb-sub">{topbar.content.sub}</div>}
            </div>
            {topbar.content?.actions && <div className="flex items-center gap-2.5">{topbar.content.actions}</div>}
          </header>
          <main className="scroll">
            <div className="stack">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    </TopbarContext.Provider>
  );
}
